import { useContext } from 'react'

import {
  useForm, useLatestEntity, useThunkDispatch,
} from '@domain-group/react-hooks'

import { mergeAndStringifyEntityObjects } from '@functions/entity'

import PageContext from '@contexts/pageContext'

import useSelector from '@hooks/useSelector'

import defaultState, { requiredFields } from '@models/providerDeadlineGroup'

import * as providerDeadlineGroupActions from '@redux/modules/providerDeadlineGroup'

const createProviderDeadlineGroup = (params) => {
  const {
    dispatch, providerDeadlineGroup, providerDeadlineGroupParams, requestOptions,
  } = params
  const { createProviderDeadlineGroup: createFn } = providerDeadlineGroupActions

  const stringifyObjectKeys = ['deadlines']
  const updatedParams = mergeAndStringifyEntityObjects(
    providerDeadlineGroup,
    providerDeadlineGroupParams,
    stringifyObjectKeys,
  )

  return dispatch(createFn(updatedParams, requestOptions))
}

const updateProviderDeadlineGroup = (params) => {
  const {
    dispatch, providerDeadlineGroupParams, requestOptions,
  } = params
  const { updateProviderDeadlineGroup: updateFn } = providerDeadlineGroupActions

  const updatedParams = {
    ...providerDeadlineGroupParams,
    deadlines: JSON.stringify(providerDeadlineGroupParams.deadlines),
  }

  return dispatch(updateFn(updatedParams, requestOptions))
}

const deleteProviderDeadlineGroup = ({ providerDeadlineGroup, dispatch }) => {
  const { deleteProviderDeadlineGroup: deleteFn } = providerDeadlineGroupActions
  return dispatch(deleteFn(providerDeadlineGroup))
}

const setAsDefault = (params) => {
  const { dispatch, providerDeadlineGroup, requestOptions } = params
  const { setAsDefault: setAsDefaultFn } = providerDeadlineGroupActions

  return dispatch(setAsDefaultFn(providerDeadlineGroup, requestOptions))
}

const createOrEditProviderDeadlineGroup = params => new Promise((resolve, reject) => {
  const { providerDeadlineGroup, showCreateOrEditProviderDeadlineGroupModal } = params

  if (showCreateOrEditProviderDeadlineGroupModal){
    const payload = {
      providerDeadlineGroup,
    }

    showCreateOrEditProviderDeadlineGroupModal(payload)

    resolve({ success: true, result: payload })
  }

  reject(new Error('showCreateOrEditProviderDeadlineGroupModal not defined in PageContext callbacks'))
})

export function useProviderDeadlineGroupForm(providerDeadlineGroup = {}, options = {}){
  const { validateOn } = options

  const providerDeadlineGroupForm = useForm(
    defaultState,
    { entity: providerDeadlineGroup, requiredFields, validateOn },
    [providerDeadlineGroup.id, providerDeadlineGroup.cache_key],
  )

  return {
    ...providerDeadlineGroupForm,
  }
}

export const useRelations = (providerDeadlineGroup) => {
  const { provider_id } = providerDeadlineGroup || {}

  const { providers } = useSelector(reduxState => reduxState.entities)

  const provider = provider_id && providers[provider_id] ? providers[provider_id] : {}

  return {
    provider,
  }
}

const useProviderDeadlineGroup = (initEntity = {}) => {
  const { callbacks } = useContext(PageContext)
  const { showCreateOrEditProviderDeadlineGroupModal } = callbacks || {}

  const { entity: providerDeadlineGroup } = useLatestEntity(initEntity, 'providerDeadlineGroups')

  const {
    creating, deleting, loading, updating,
  } = useSelector(reduxState => reduxState.providerDeadlineGroups)

  const dispatch = useThunkDispatch()

  const { provider } = useRelations(providerDeadlineGroup)

  const deadlines = providerDeadlineGroup.deadlines || {}
  const filteredDeadlines = Object.values(deadlines).filter(deadline => !!deadline?.time)
  const deadlineCount = filteredDeadlines.length

  return {
    callbacks: {
      createOrEditProviderDeadlineGroup: () => (
        createOrEditProviderDeadlineGroup({ providerDeadlineGroup, showCreateOrEditProviderDeadlineGroupModal })
      ),
      createProviderDeadlineGroup: (providerDeadlineGroupParams, requestOptions) => createProviderDeadlineGroup({
        providerDeadlineGroup, providerDeadlineGroupParams, dispatch, requestOptions,
      }),
      deleteProviderDeadlineGroup: () => deleteProviderDeadlineGroup({ providerDeadlineGroup, dispatch }),
      setAsDefault: requestOptions => setAsDefault({ providerDeadlineGroup, dispatch, requestOptions }),
      updateProviderDeadlineGroup: (providerDeadlineGroupParams, requestOptions) => updateProviderDeadlineGroup({
        providerDeadlineGroupParams, dispatch, requestOptions,
      }),
    },
    creating,
    deadlineCount,
    deleting,
    loading,
    provider,
    updating,
    providerDeadlineGroup,
  }
}

export default useProviderDeadlineGroup
